// src/components/onboarding/AvailabilityForm.tsx
//
// Griglia disponibilità (Exhibit A del Co-hosting): per ogni mese il proprietario
// imposta Open/Blocked per Month / 2 Weeks / Week / Weekend / Midweek e il Minimum Stay.
import React from 'react';
import { AvailabilityData, AvailabilityMonth, AvailabilityState } from '../../types';

export const AVAILABILITY_MONTHS: { key: string; label: string }[] = [
    { key: 'january', label: 'January' }, { key: 'february', label: 'February' },
    { key: 'march', label: 'March' }, { key: 'april', label: 'April' },
    { key: 'may', label: 'May' }, { key: 'june', label: 'June' },
    { key: 'july', label: 'July' }, { key: 'august', label: 'August' },
    { key: 'september', label: 'September' }, { key: 'october', label: 'October' },
    { key: 'november', label: 'November' }, { key: 'december', label: 'December' },
];

const PERIOD_COLS: { key: keyof Omit<AvailabilityMonth, 'minstay'>; label: string }[] = [
    { key: 'month', label: 'Month' },
    { key: 'twoweeks', label: '2 Weeks' },
    { key: 'week', label: 'Week' },
    { key: 'weekend', label: 'Weekend' },
    { key: 'midweek', label: 'Midweek' },
];

const emptyMonth = (): AvailabilityMonth => ({
    month: 'Open', twoweeks: 'Open', week: 'Open', weekend: 'Open', midweek: 'Open', minstay: '2',
});

/** Costruisce una disponibilità di default (tutti i mesi Open, minimum stay 2). */
export function buildDefaultAvailability(): AvailabilityData {
    const data: AvailabilityData = {};
    for (const m of AVAILABILITY_MONTHS) data[m.key] = emptyMonth();
    return data;
}

/** Normalizza dati parziali/mancanti in una griglia completa 12 mesi. */
export function normalizeAvailability(input?: AvailabilityData | null): AvailabilityData {
    const data = buildDefaultAvailability();
    if (input) {
        for (const m of AVAILABILITY_MONTHS) {
            const src = input[m.key];
            if (src) data[m.key] = { ...data[m.key], ...src };
        }
    }
    return data;
}

interface Props {
    value: AvailabilityData;
    onChange: (next: AvailabilityData) => void;
}

const AvailabilityForm: React.FC<Props> = ({ value, onChange }) => {
    const grid = normalizeAvailability(value);

    const setCell = (monthKey: string, col: keyof AvailabilityMonth, cellVal: string) => {
        onChange({ ...grid, [monthKey]: { ...grid[monthKey], [col]: cellVal } });
    };

    const toggle = (monthKey: string, col: keyof Omit<AvailabilityMonth, 'minstay'>) => {
        const cur = grid[monthKey][col] as AvailabilityState;
        setCell(monthKey, col, cur === 'Open' ? 'Blocked' : 'Open');
    };

    // Imposta un intero mese a Open o Blocked in un colpo solo.
    const setWholeMonth = (monthKey: string, state: AvailabilityState) => {
        onChange({
            ...grid,
            [monthKey]: {
                ...grid[monthKey],
                month: state, twoweeks: state, week: state, weekend: state, midweek: state,
            },
        });
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="text-gray-500">
                            <th className="text-left font-bold px-2 py-2">Period</th>
                            {PERIOD_COLS.map((c) => (
                                <th key={c.key} className="font-bold px-1 py-2 text-center whitespace-nowrap">{c.label}</th>
                            ))}
                            <th className="font-bold px-1 py-2 text-center whitespace-nowrap">Min. Stay</th>
                            <th className="px-1 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {AVAILABILITY_MONTHS.map((m) => (
                            <tr key={m.key} className="border-t border-gray-100">
                                <td className="px-2 py-1.5 font-semibold text-gray-700 whitespace-nowrap">{m.label}</td>
                                {PERIOD_COLS.map((c) => {
                                    const open = grid[m.key][c.key] === 'Open';
                                    return (
                                        <td key={c.key} className="px-1 py-1.5 text-center">
                                            <button
                                                type="button"
                                                onClick={() => toggle(m.key, c.key)}
                                                className={`w-full min-w-[74px] px-2 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                                                    open
                                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                                }`}
                                                aria-label={`${m.label} ${c.label}: ${open ? 'Open' : 'Blocked'}`}
                                            >
                                                {open ? 'Open' : 'Blocked'}
                                            </button>
                                        </td>
                                    );
                                })}
                                <td className="px-1 py-1.5 text-center">
                                    <input
                                        type="number"
                                        min={1}
                                        value={grid[m.key].minstay}
                                        onChange={(e) => setCell(m.key, 'minstay', e.target.value)}
                                        className="w-16 text-center px-2 py-1.5 rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none"
                                        aria-label={`${m.label} minimum stay`}
                                    />
                                </td>
                                <td className="px-1 py-1.5 whitespace-nowrap text-center">
                                    <button
                                        type="button"
                                        onClick={() => setWholeMonth(m.key, 'Open')}
                                        className="text-[11px] font-bold text-green-600 hover:underline mr-2"
                                    >
                                        All open
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setWholeMonth(m.key, 'Blocked')}
                                        className="text-[11px] font-bold text-gray-500 hover:underline"
                                    >
                                        All blocked
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-gray-400 mt-3">
                Tap a cell to toggle between <span className="font-semibold text-green-600">Open</span> and{' '}
                <span className="font-semibold text-gray-600">Blocked</span>. This fills Exhibit A of your Co-hosting Agreement.
            </p>
        </div>
    );
};

export default AvailabilityForm;
